import * as React from "react";
import Image from "next/image";
import { useMDXComponent } from "@content-collections/mdx/react";
import { cn } from "~/lib/utils";

import { Comparison, Timeline } from "./diagrams";
import { YouTubeEmbed } from "@next/third-parties/google";
import { getQuizById } from "~/config/quizzes";
import Link from "next/link";
import { Alert, AlertTitle, AlertDescription } from "~/components/ui/alert";
import {
  ArrowRight,
  CirclePlay,
  Quote,
  Lightbulb,
  AlertTriangle,
  BookOpen,
} from "lucide-react";
import Markdown from "react-markdown";
import { AiTherapistPromotionBlog } from "~/components/quiz/ai-therapist-promotion";
import { TherapyPriceMonitor } from "~/components/blog/therapy-price-monitor";
import type { TherapyPriceMarketSnapshot } from "~/server/marketing/therapy-price-market";

/**
 * MDX Components - Redesigned for optimal blog typography
 *
 * Typography principles applied:
 * - Golden ratio (1.618) for type scale hierarchy
 * - Optimal line length: 45-80 characters (max-w-prose = ~65ch)
 * - Line height: 1.5-1.75x for body text readability
 * - Asymmetric spacing: more above headings, less below
 * - Clear visual hierarchy through size, weight, and spacing
 * - Mobile-first responsive design
 * - Relative units (rem) for accessibility
 *
 * Type scale (mobile → desktop):
 * - Body: 1rem/16px → 1.125rem/18px (base)
 * - H6: 1rem → 1.125rem (1x base)
 * - H5: 1.125rem → 1.25rem (1.1x)
 * - H4: 1.25rem → 1.5rem (1.33x)
 * - H3: 1.5rem → 1.875rem (1.67x)
 * - H2: 1.875rem → 2.25rem (2x)
 * - H1: 2.25rem → 3rem (2.67x)
 */

export const StatHighlight = ({
  value,
  label,
  source,
  sourceUrl,
}: {
  value: string;
  label: string;
  source?: string;
  sourceUrl?: string;
}) => (
  <div className="my-6 border-l-[3px] border-brand py-1 pl-6 md:my-8 md:border-l-4 md:pl-8">
    <p className="text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
      {value}
    </p>
    <p className="mt-1.5 text-base leading-snug text-foreground/70 md:text-lg">
      {label}
    </p>
    {source && (
      <p className="mt-2 text-xs text-muted-foreground">
        {sourceUrl ? (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-muted-foreground/40 underline-offset-2 hover:text-foreground"
          >
            {source}
          </a>
        ) : (
          source
        )}
      </p>
    )}
  </div>
);

// Ratified palette (2026-08-06): callouts sit on warm brand surfaces and are
// differentiated by icon and title. Amber stays reserved for warnings only.
const INFO_BOX_VARIANTS = {
  tip: {
    icon: Lightbulb,
    className: "bg-editorial-quiet",
    defaultTitle: "Savet",
  },
  practical: {
    icon: Lightbulb,
    className: "bg-brand/10",
    defaultTitle: "Praktično",
  },
  warning: {
    icon: AlertTriangle,
    className: "bg-amber-50",
    defaultTitle: "Važno",
  },
  "key-takeaway": {
    icon: BookOpen,
    className: "bg-brand/15",
    defaultTitle: "Ključni zaključak",
  },
} as const;

export const InfoBox = ({
  variant = "tip",
  title,
  children,
}: {
  variant?: string;
  title?: string;
  children: React.ReactNode;
}) => {
  const config =
    INFO_BOX_VARIANTS[variant as keyof typeof INFO_BOX_VARIANTS] ??
    INFO_BOX_VARIANTS.tip;
  const Icon = config.icon;

  return (
    <Alert
      className={cn(
        "my-6 md:my-8 [&>svg]:top-[18px] [&>svg]:h-5 [&>svg]:w-5",
        config.className,
      )}
    >
      <Icon className="hidden md:block" />
      <AlertTitle className="text-base font-semibold md:text-lg">
        {title ?? config.defaultTitle}
      </AlertTitle>
      <AlertDescription className="text-sm leading-relaxed md:text-base [&>p:last-child]:mb-0">
        {children}
      </AlertDescription>
    </Alert>
  );
};

export const VideoEmbed = ({
  videoid,
  title,
  source,
}: {
  videoid: string;
  title?: string;
  source?: string;
}) => (
  <figure className="my-6 md:my-8">
    <div className="overflow-hidden rounded-xl border border-border/60 shadow-xs [&_lite-youtube]:max-w-full">
      <YouTubeEmbed videoid={videoid} style="max-width: 100%;" />
    </div>
    {(title || source) && (
      <figcaption className="mt-2.5 flex items-center gap-1.5 text-xs text-muted-foreground md:text-sm">
        <CirclePlay
          className="h-3.5 w-3.5 shrink-0 translate-y-px"
          aria-hidden="true"
        />
        <span>
          {title}
          {title && source && " — "}
          {source && <>Izvor: {source}</>}
        </span>
      </figcaption>
    )}
  </figure>
);

/**
 * A quiz is a four-minute self-check, so it gets the weight of a footnote rather
 * than a card. Previously this rendered as a filled callout with an icon and a
 * pill button, which gave a small optional aside the same visual weight as
 * booking therapy and made every offer in an article look like an ad slot. Now
 * it is a hairline, one sentence in the article's own voice, and a link.
 */
export const QuizRecommendation = ({ quizId }: { quizId: string }) => {
  const recommendedQuiz = getQuizById(quizId);

  if (!recommendedQuiz) {
    return null;
  }

  const questionCount = recommendedQuiz.questions?.length;

  return (
    <div className="my-7 border-t border-border pt-4 md:my-9">
      <p className="m-0 max-w-[62ch] text-[15px] leading-relaxed text-foreground md:text-base">
        {recommendedQuiz.description}{" "}
        <Link
          href={`/testovi/${quizId}`}
          className="whitespace-nowrap font-semibold text-brand underline decoration-brand/30 underline-offset-[3px] transition-colors hover:decoration-brand"
        >
          {recommendedQuiz.shortTitle ?? recommendedQuiz.title}
          <ArrowRight className="ml-1 inline h-3.5 w-3.5 align-[-2px]" />
        </Link>
      </p>
      <p
        className="m-0 mt-1.5 text-xs text-muted-foreground"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {questionCount ? `${questionCount} pitanja, ` : ""}
        {recommendedQuiz.duration}
      </p>
    </div>
  );
};

/**
 * Therapist matching. Deliberately the only forest surface an article uses, so
 * it reads as the one substantive next step rather than another cream callout.
 *
 * The copy states what is actually visible before booking rather than promising
 * a good match, because showing the thing instead of describing it is the brand's
 * own trust mechanism. White is used here only as foreground contrast on a brand
 * field, which is its one sanctioned role.
 */
export const TherapistMatch = ({
  lead = "Ne moraš da pogađaš koji terapeut ti odgovara.",
}: {
  lead?: string;
}) => (
  <aside className="my-8 rounded-xl bg-brand-forest px-5 py-6 md:my-10 md:px-7 md:py-7">
    <p className="m-0 max-w-[42ch] text-lg font-semibold leading-snug text-white md:text-xl">
      {lead}
    </p>
    <p className="m-0 mt-2.5 max-w-[54ch] text-sm leading-relaxed text-white/70 md:text-[15px]">
      Pre nego što zakažeš, vidiš kako terapeut radi, koliko naplaćuje, video
      predstavljanje i koje termine ima slobodne.
    </p>
    <Link
      href="/upitnik"
      className="mt-5 inline-flex items-center gap-2 rounded-lg bg-background px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white"
    >
      Pokreni upitnik
      <ArrowRight className="h-4 w-4" />
    </Link>
    <p
      className="m-0 mt-3 text-xs text-white/60"
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      12 kratkih pitanja, oko 2 minuta
    </p>
  </aside>
);

const baseComponents = {
  h1: ({ className, ...props }: { className?: string }) => (
    <h1
      className={cn(
        "text-4xl font-bold tracking-tight md:text-5xl",
        "mb-4 mt-10 first:mt-0 md:mb-5 md:mt-14",
        "scroll-m-20 text-balance",
        className,
      )}
      {...props}
    />
  ),

  h2: ({ className, ...props }: { className?: string }) => (
    <h2
      className={cn(
        "font-sans text-3xl font-medium tracking-tight md:text-4xl",
        "mb-3 mt-10 first:mt-0 md:mb-4 md:mt-12",
        "scroll-m-20 text-balance",
        className,
      )}
      {...props}
    />
  ),

  h3: ({ className, ...props }: { className?: string }) => (
    <h3
      className={cn(
        "font-sans text-2xl font-medium tracking-tight md:text-3xl",
        "mb-2 mt-8 first:mt-0 md:mb-3 md:mt-10",
        "scroll-m-20 text-balance",
        className,
      )}
      {...props}
    />
  ),

  h4: ({ className, ...props }: { className?: string }) => (
    <h4
      className={cn(
        "font-sans text-xl font-medium tracking-tight md:text-2xl",
        "mb-2 mt-6 first:mt-0 md:mb-2.5 md:mt-8",
        "scroll-m-20 text-pretty",
        className,
      )}
      {...props}
    />
  ),

  h5: ({ className, ...props }: { className?: string }) => (
    <h5
      className={cn(
        "text-lg font-semibold md:text-xl",
        "mb-1.5 mt-5 first:mt-0 md:mb-2 md:mt-6",
        "scroll-m-20 text-pretty",
        className,
      )}
      {...props}
    />
  ),

  h6: ({ className, ...props }: { className?: string }) => (
    <h6
      className={cn(
        "text-base font-semibold uppercase tracking-wide md:text-lg",
        "mb-1.5 mt-4 first:mt-0 md:mb-2 md:mt-5",
        "scroll-m-20 text-pretty text-foreground/90",
        className,
      )}
      {...props}
    />
  ),

  p: ({ className, ...props }: { className?: string }) => (
    <p
      className={cn(
        "font-sans text-base leading-7 md:text-lg md:leading-8",
        "mb-5 md:mb-6",
        "text-pretty text-foreground",
        className,
      )}
      {...props}
    />
  ),

  a: ({ className, href, ...props }: { className?: string; href?: string }) => {
    const isExternal = href?.startsWith("http");

    return (
      <a
        href={href}
        className={cn(
          "font-medium text-brand",
          "underline decoration-brand/40 decoration-[1.5px] underline-offset-[3px]",
          "transition-colors duration-200",
          "hover:text-brand/80 hover:decoration-brand/60",
          "wrap-break-word",
          className,
        )}
        {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
        {...props}
      />
    );
  },

  ul: ({ className, ...props }: { className?: string }) => (
    <ul
      className={cn(
        "font-sans text-base leading-7 md:text-lg md:leading-8",
        "list-disc marker:text-muted-foreground/70",
        "my-5 ml-5 space-y-2.5 md:my-6 md:ml-6 md:space-y-3",
        "[&>li]:pl-1.5 md:[&>li]:pl-2",
        className,
      )}
      {...props}
    />
  ),

  ol: ({ className, ...props }: { className?: string }) => (
    <ol
      className={cn(
        "font-sans text-base leading-7 md:text-lg md:leading-8",
        "list-decimal marker:font-medium marker:text-muted-foreground/70",
        "my-5 ml-5 space-y-2.5 md:my-6 md:ml-6 md:space-y-3",
        "[&>li]:pl-1.5 md:[&>li]:pl-2",
        className,
      )}
      {...props}
    />
  ),

  li: ({ className, ...props }: { className?: string }) => (
    <li className={cn("text-pretty", className)} {...props} />
  ),

  blockquote: ({
    className,
    children,
    ...props
  }: {
    className?: string;
    children?: React.ReactNode;
  }) => (
    <blockquote
      className={cn(
        "relative",
        "font-sans text-base italic leading-7 md:text-lg md:leading-8",
        "text-foreground/80",
        "border-l-[3px] border-brand/40 md:border-l-4",
        "bg-brand/5",
        "my-6 py-4 pl-6 pr-4 md:my-8 md:py-5 md:pl-8 md:pr-6",
        "rounded-r-xl",
        "[&>p:first-child]:mt-0 [&>p:last-child]:mb-0",
        className,
      )}
      {...props}
    >
      <Quote
        className="absolute right-3 top-3 h-8 w-8 text-brand/8 md:right-4 md:h-10 md:w-10"
        aria-hidden="true"
      />
      {children}
    </blockquote>
  ),

  img: ({
    className,
    alt,
    src,
    width,
    height,
  }: React.ImgHTMLAttributes<HTMLImageElement>) =>
    typeof src === "string" && src.length > 0 ? (
      <Image
        src={src}
        alt={alt || ""}
        width={typeof width === "number" ? width : Number(width) || 1200}
        height={typeof height === "number" ? height : Number(height) || 800}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 800px"
        className={cn(
          "h-auto w-full",
          "rounded-lg border border-border/50",
          "mb-2 mt-6 md:mb-3 md:mt-8",
          className,
        )}
      />
    ) : null,

  hr: ({ ...props }) => (
    <hr
      className="my-8 border-t border-border/60 md:my-10"
      aria-hidden="true"
      {...props}
    />
  ),

  table: ({ className, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="my-6 w-full overflow-x-auto rounded-lg border border-border/60 md:my-8">
      <table
        className={cn("w-full border-collapse text-sm md:text-base", className)}
        {...props}
      />
    </div>
  ),

  thead: ({
    className,
    ...props
  }: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <thead className={cn("bg-muted/40", className)} {...props} />
  ),

  tbody: ({
    className,
    ...props
  }: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <tbody className={cn("[&>tr:last-child]:border-0", className)} {...props} />
  ),

  tr: ({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
    <tr
      className={cn(
        "border-b border-border/40 transition-colors even:bg-muted/20",
        className,
      )}
      {...props}
    />
  ),

  th: ({ className, ...props }: { className?: string }) => (
    <th
      className={cn(
        "text-left text-sm font-semibold md:text-base",
        "px-3 py-2.5 md:px-4 md:py-3",
        "[[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),

  td: ({ className, ...props }: { className?: string }) => (
    <td
      className={cn(
        "text-left text-sm md:text-base",
        "px-3 py-2.5 md:px-4 md:py-3",
        "[[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),

  pre: ({ className, ...props }: { className?: string }) => (
    <pre
      className={cn(
        "font-mono text-xs leading-relaxed md:text-sm",
        "bg-muted/40",
        "border border-border/50",
        "my-5 overflow-x-auto rounded-lg p-4 md:my-6",
        className,
      )}
      {...props}
    />
  ),

  code: ({ className, ...props }: { className?: string }) => (
    <code
      className={cn(
        "font-mono text-[0.9em] font-medium",
        "rounded-sm border border-border/50 bg-muted/50 px-1.5 py-0.5",
        className,
      )}
      {...props}
    />
  ),

  i: ({ className, ...props }: { className?: string }) => (
    <i className={cn("italic", className)} {...props} />
  ),

  em: ({
    className,
    children,
    ...props
  }: {
    className?: string;
    children: React.ReactNode;
  }) => {
    const isImageCredit =
      typeof children === "string" &&
      (children.startsWith("Foto:") ||
        children.startsWith("Izvor:") ||
        children.startsWith("Credit:"));

    if (isImageCredit) {
      return (
        <em
          className={cn(
            "block text-xs not-italic text-muted-foreground md:text-sm",
            "-mt-1 mb-5 md:mb-6",
            className,
          )}
          {...props}
        >
          {children}
        </em>
      );
    }

    return (
      <em className={cn("italic", className)} {...props}>
        {children}
      </em>
    );
  },

  strong: ({ className, ...props }: { className?: string }) => (
    <strong
      className={cn("font-semibold text-foreground", className)}
      {...props}
    />
  ),

  b: ({ className, ...props }: { className?: string }) => (
    <strong className={cn("font-bold text-foreground", className)} {...props} />
  ),

  dl: ({ className, ...props }: { className?: string }) => (
    <dl
      className={cn("my-5 space-y-4 md:my-6 md:space-y-5", className)}
      {...props}
    />
  ),

  dt: ({ className, ...props }: { className?: string }) => (
    <dt
      className={cn("text-base font-semibold md:text-lg", className)}
      {...props}
    />
  ),

  dd: ({ className, ...props }: { className?: string }) => (
    <dd
      className={cn(
        "ml-4 text-base leading-7 text-foreground/80 md:ml-5 md:text-lg md:leading-8",
        className,
      )}
      {...props}
    />
  ),

  // Next.js Image component
  Image,

  // YouTube embed
  YouTubeEmbed,

  // Custom components
  QuizRecommendation,
  TherapistMatch,
  AiTherapistPromotionBlog,
  StatHighlight,
  InfoBox,
  VideoEmbed,

  // Inline diagrams
  Timeline,
  Comparison,
};

interface MdxProps {
  code: string;
  therapyPriceSnapshot?: TherapyPriceMarketSnapshot | null;
}

export function Mdx({ code, therapyPriceSnapshot }: MdxProps) {
  const Component = useMDXComponent(code);
  const components = {
    ...baseComponents,
    TherapyPriceMonitor: () => (
      <TherapyPriceMonitor snapshot={therapyPriceSnapshot ?? null} />
    ),
  };

  return (
    <div className="mdx">
      <Component components={components} />
    </div>
  );
}

export const MarkdownWithComponents = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // @ts-expect-error types are not ideal here but it works
  return <Markdown components={baseComponents}>{children}</Markdown>;
};

export const MarkdownWithoutComponents = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  // @ts-expect-error types are not ideal here but it works
  return <Markdown className={className}>{children}</Markdown>;
};
